import { useEffect, useMemo, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { ManualPayslipDialog } from "../../talentProfile/Employment/PayslipOverview/ManualPayslipDialog";
import { SalariesHeader } from "../SalariesHeader";
import { WageReportDeliveryDetailsModal } from "../wageReports/WageReportDeliveryDetails";
import { WageReportDeliveryDetails } from "../wageReports/wageReportsAPI";
import { fetchWageReportById } from "../wageReports/wageReportsSlice";
import { PayslipDetails } from "./PayslipDetails";
import { PayslipsTable, RowField } from "./PayslipsTable";
import { Payslip, PayslipDetailed } from "./payslipAPI";
import {
  fetchMorePayslips,
  fetchPayslipById,
  fetchPayslips,
  selectAll,
} from "./payslipsSlice";
import { PAYSLIPS_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { useTablePagination } from "../../../hooks/useTablePagination";
import { TablePaginationProps } from "../../../common/components/functional/pagination/types";
import {
  formatSalaryPagePayslipData,
  formatPayslipOverviewData,
} from "./rowHelpers";
import { useNavigate } from "react-router-dom";
import useIsLoading from "../../../hooks/useIsLoading";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { Box } from "@mui/material";

type Props = {
  filters: SearchFilter[];
  rowFields: RowField[];
  showTotals?: boolean;
  talentId?: string;
  addPayslip?: boolean;
  canEditPayslipDates?: boolean;
  showTalentView?: boolean;
  tableContainerSx?: Record<string, any>;
};

export const Payslips = ({
  filters,
  rowFields,
  showTotals,
  talentId,
  addPayslip,
  canEditPayslipDates = false,
  showTalentView = false,
  tableContainerSx = {},
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const payslips = useAppSelector((state) => selectAll(state));
  const isLoading = useIsLoading(["payslips"]);
  const { cursor, hits } = useAppSelector((state) => state.payslips);

  const [filterObject, setFilterObject] = useState<Record<string, any>>({});
  const [payslipDetailed, setPayslipDetailed] = useState<
    PayslipDetailed | undefined
  >();
  const [isPayslipDetailsModalOpen, setIsPayslipDetailsModalOpen] =
    useState(false);

  const [isManualPayslipDialogpOpen, setIsManualPayslipDialogpOpen] =
    useState<boolean>(false);
  const [payslipToEdit, setPayslipToEdit] = useState<Payslip | null>(null);

  const [wageReportDetailed, setWageReportDetailed] = useState<
    WageReportDeliveryDetails | undefined
  >();
  const [isDeliveryDetailsModalOpen, setIsDeliveryDetailsModalOpen] =
    useState(false);

  const editPayslip = (id: string) => {
    setPayslipToEdit(payslips.find((p) => p.id === id)!!);
    setIsManualPayslipDialogpOpen(true);
  };

  const openPayslipDetails = (id: string) => {
    setIsPayslipDetailsModalOpen(true);
    dispatch(
      fetchPayslipById({
        id,
        languageCode: i18n.language,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setPayslipDetailed(action.payload);
      }
    });
  };

  const openWageReportDetails = (id: string | null) => {
    if (!id) return;
    setIsDeliveryDetailsModalOpen(true);
    dispatch(fetchWageReportById(id)).then((action) => {
      if (isFulfilled(action)) {
        setWageReportDetailed(action.payload);
      }
    });
  };

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<Payslip>({
      data: payslips,
      cursor,
      hits,
      pageSize: parseInt(PAYSLIPS_PAGE_SIZE, 10),
      fetchNext: (cursor: string) =>
        fetchMorePayslips({ cursor, filters: filterObject }),
    });

  useEffect(() => {
    setFilterObject(
      filters.reduce((acc, filter) => {
        const { name, value } = filter;
        if (name === "type") return acc;
        return { ...acc, [name]: value };
      }, {}),
    );
  }, [filters]);

  useEffect(() => {
    dispatch(
      fetchPayslips({
        cursor: null,
        filters: filterObject,
      }),
    );
    resetPagination();
  }, [dispatch, filterObject, resetPagination]);

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize: parseInt(PAYSLIPS_PAGE_SIZE, 10),
  };

  const rows = !showTalentView
    ? formatSalaryPagePayslipData(
        pageData,
        openPayslipDetails,
        openWageReportDetails,
        navigate,
      )
    : formatPayslipOverviewData(pageData, openPayslipDetails);

  const memoizedRows = useMemo(() => rows, [rows]);

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <SalariesHeader pagination={paginationProps} isLoading={isLoading} />
          <PayslipDetails
            payslip={payslipDetailed}
            isOpen={isPayslipDetailsModalOpen}
            onClose={() => {
              setIsPayslipDetailsModalOpen(false);
              setPayslipDetailed(undefined);
            }}
          />
          <ManualPayslipDialog
            isOpen={isManualPayslipDialogpOpen}
            closeDialog={() => {
              setIsManualPayslipDialogpOpen(false);
              setPayslipToEdit(null);
            }}
            talentId={talentId}
            payslip={payslipToEdit}
          />
          <WageReportDeliveryDetailsModal
            wageReport={wageReportDetailed}
            isOpen={isDeliveryDetailsModalOpen}
            onClose={() => {
              setIsDeliveryDetailsModalOpen(false);
              setWageReportDetailed(undefined);
            }}
          />
          {!!rows && (
            <Box sx={{ ...tableContainerSx }}>
              <PayslipsTable
                rowFields={rowFields}
                rows={memoizedRows}
                showTotals={showTotals}
                editRow={canEditPayslipDates ? editPayslip : undefined}
                addItem={
                  addPayslip
                    ? () => setIsManualPayslipDialogpOpen(true)
                    : undefined
                }
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};
